import {createStyles} from '@mantine/core';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import {getDefaultStudiesSearchParams} from 'studies/workspace/StudySearchHelpers';
import {ButtonSize} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

import CreateCrossStudyAnalysisButton, {
  getSelectedStudies,
  hasCreateAnalysisCapability,
} from './CreateCrossStudyAnalysisButton';
import NewStudyDropdown from './NewStudyDropdown';

const useStyles = createStyles((theme) => ({
  analysisButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: '10px',
    paddingBottom: '2px',
  },
}));

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
}

type StudiesSidepanelProps = SidepanelProps & SearchProps;

export function StudiesSidepanel(props: StudiesSidepanelProps & SearchProps): JSX.Element {
  const showAnalyzeButton =
    BenchlingConfig.STUDIES_ENABLE_CROSS_STUDY_ANALYSIS && hasCreateAnalysisCapability();
  const {classes} = useStyles();
  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.STUDIES_SIDEPANEL}
        savedSearchSection={SearchSection.STUDIES}
        additionalHeaderButtons={
          <NewStudyDropdown
            actionOrigin={ActionOrigin.STUDIES_SIDEPANEL}
            buttonSize={ButtonSize.SMALL}
            buttonTooltipProps={{
              text: 'Create...',
              placement: FloatingPlacement.BOTTOM,
            }}
          />
        }
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
        ]}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <StudiesSidepanelCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        availableTypes={[ModelTypeConstants.STUDY]}
        extraPrimaryFilterOptions={[]}
        preservedFilterKeys={[]}
        section={Section.STUDIES}
        rowSelectionMode={showAnalyzeButton ? RowSelectionMode.MULTIPLE : undefined}
        renderAdditionalToolbarButtons={
          showAnalyzeButton
            ? (items, selectedIds) => (
                <div className={classes.analysisButtonContainer}>
                  <CreateCrossStudyAnalysisButton
                    selectedStudies={getSelectedStudies(items, selectedIds)}
                    actionOrigin={ActionOrigin.STUDIES_SIDEPANEL}
                  />
                </div>
              )
            : undefined
        }
      />
    </div>
  );
}

function StudiesSidepanelCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="studies"
            text="Studies"
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, {
                  preservedFilterKeys: [],
                  filtersToReset: getDefaultStudiesSearchParams().filters,
                })
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs crumbs={[<span key="root">Studies</span>]} settingsIcon={props.settingsComponents} />
  );
}

export function getStudiesURL(
  prevProps: StudiesSidepanelProps,
  props: StudiesSidepanelProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/studies/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(StudiesSidepanel, SearchType.STUDIES, SECTIONS.STUDIES, {
  defaultSearchParams: getDefaultStudiesSearchParams(),
  getNavigationUpdate: getStudiesURL,
});
